import { Close } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  styled,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { InstagramEmbed } from 'react-social-media-embed';
import * as v from 'valibot';

import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { useIsAfterDate } from '@/hooks/useIsAfterDate.ts';
import { useTimer } from '@/hooks/useTimer.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { CompetitionWinnerSchema, type CompetitionWinner } from '@/lib/api/requests.ts';
import { numeric } from '@/lib/schema-utils.ts';
import type { NoDate } from '@/services/new-utils.ts';
import { Button } from '@/ui/Button.tsx';

import { Snowfall } from '../../../_common/Snowfall.tsx';
import { advent2024DrawDates, getNextAdvent2024DrawDate } from '../../../config.ts';
import ForestLeft from '../_assets/forest-left.svg?styled';
import ForestRight from '../_assets/forest-right.svg?styled';
import Horse from '../_assets/horse.svg?styled';
import MountainsBG from '../_assets/mountains-bg.svg?styled';
import RedHouse from '../_assets/red-house.svg?styled';
import RibbonBigBottom from '../_assets/ribbon-big-bottom.svg?styled';
import RibbonBigTop from '../_assets/ribbon-big-top.svg?styled';
import RibbonSmallBottom from '../_assets/ribbon-small-bottom.svg?styled';
import SnowFrontDark from '../_assets/snow-front-dark.svg?styled';
import SnowFront from '../_assets/snow-front.svg?styled';
import Snow from '../_assets/snow.svg?styled';
import Snowman from '../_assets/snowman.svg?styled';
import TileBgCanes from '../_assets/tile-bg-canes.svg?styled';
import TileBgGingerbread from '../_assets/tile-bg-gingerbread.svg?styled';
import TileBgSnow from '../_assets/tile-bg-snow.svg?styled';
import TileBgTree from '../_assets/tile-bg-tree.svg?styled';
import TreeWithLights from '../_assets/tree-with-lights.svg?styled';
import Tree from '../_assets/tree.svg?styled';
import WhiteHouse from '../_assets/white-house.svg?styled';
import { CTA } from '../_components/CTA.tsx';

const Root = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateAreas: `
    "main"
    "disclaimer"
    "below"
  `,
  '--min-height': '700px',
  gridTemplateRows:
    'minmax(var(--min-height), min(calc(100vh - var(--top-bar-height)), 1200px)) auto auto',
  gridTemplateColumns: '1fr auto',
  width: '100%',
  background: '#072453',
  color: 'white',
  isolation: 'isolate',

  [theme.breakpoints.up('lg')]: {
    '--min-height': '900px',
  },
}));

export type TopSectionProps = {
  winners: CompetitionWinner[];
};

export const TopSection: React.FC<TopSectionProps> = ({ winners }) => {
  return (
    <Root>
      <Illustration />
      <Disclaimer />
      <Box
        sx={{
          gridArea: 'below',
          backgroundColor: 'white',
          color: (theme) => theme.palette.text.default,
        }}
      />

      <Container
        maxWidth="xl"
        sx={{
          display: 'grid',
          pt: { xs: 4, md: 8, lg: 10 },
          gridArea: 'main / main / below / below',
          gridTemplateRows: 'subgrid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            xl: 'minmax(0, 1fr) minmax(0, 1fr)',
          },
          gap: 6,
        }}
      >
        <Content />
        <Box
          sx={{
            gridArea: { xs: 'below', xl: 'auto' },
            // Workaround for safari
            gridColumn: { xs: '1', xl: 'auto' },
          }}
        >
          <Calendar winners={winners} />
        </Box>
      </Container>
    </Root>
  );
};

const Disclaimer = () => {
  return (
    <Box
      sx={{
        gridArea: 'disclaimer',
        backgroundColor: 'white',
        color: (theme) => theme.palette.text.default,
        py: 2,
      }}
    >
      <Container maxWidth="md">
        <div className="text-center typography-v1-body4 text-balance">
          <LoanExampleText />
        </div>
      </Container>
    </Box>
  );
};

const days = Array.from({ length: 24 }, (_, i) => i + 1);

const CalendarGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  position: 'relative',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '12px',
  maxWidth: '625px',
  width: '100%',
  marginInline: 'auto',

  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',

    [`& > .day-24`]: {
      gridColumn: 'span 2',
      aspectRatio: 'unset',
    },
  },
}));

const AdventWinnerSchema = v.object({
  ...CompetitionWinnerSchema.entries,
  meta: v.object({
    day: numeric(),
    instagramPostUrl: v.optional(v.string()),
    tileLine1: v.optional(v.string(), '\xa0'),
    tileLine2: v.optional(v.string(), '\xa0'),
  }),
});

type AdventWinner = v.InferOutput<typeof AdventWinnerSchema>;

const schema = v.array(AdventWinnerSchema);

type CalendarProps = {
  winners: CompetitionWinner[];
};

const Calendar: React.FC<CalendarProps> = ({ winners: winnersProp }) => {
  const winners = useMemo(() => v.parse(schema, winnersProp), [winnersProp]);
  const winnersByDay = useMemo(
    () =>
      winners.reduce(
        (acc, winner) => {
          acc[winner.meta.day] ??= [];
          acc[winner.meta.day]!.push(winner);
          return acc;
        },
        {} as Record<number, AdventWinner[] | undefined>,
      ),
    [winners],
  );
  const isAfterFirstAdventDraw = useIsAfterDate(advent2024DrawDates[0]);
  const timer = useTimer(getNextAdvent2024DrawDate());

  return (
    <CalendarGrid>
      {days.map((day) => (
        <CalendarTile key={day} day={day} winners={winnersByDay[day] || []} />
      ))}
      {!isAfterFirstAdventDraw && (
        <Box
          sx={{
            position: 'absolute',
            backgroundColor: '#EC4949',
            color: 'white',
            top: '50%',
            left: 0,
            right: 0,
            transform: 'translateY(-50%)',
          }}
        >
          <RibbonBigTop
            sx={{
              position: 'absolute',
              bottom: 'calc(100% - 1px)',
              left: '50%',
              transform: 'translateX(-50%)',
              scale: { sm: 1.5 },
              transformOrigin: '0 100%',
            }}
          />
          <Stack sx={{ textAlign: 'center', p: { xs: 1, md: 0 } }}>
            <p className="-mb-2 typography-v1-body2">Åpnes om</p>
            <p className="-mb-1 typography-v1-h1">{timer.days} dager</p>
            <p className="typography-v1-h4">
              {timer.hours} timer : {timer.minutes} minutter : {timer.seconds} sekunder
            </p>
          </Stack>
          <RibbonBigBottom
            sx={{
              position: 'absolute',
              top: 'calc(100% - 1px)',
              left: '50%',
              transform: 'translateX(-50%)',
              scale: { sm: 2 },
              transformOrigin: '0 0',
            }}
          />
        </Box>
      )}
    </CalendarGrid>
  );
};

type CalendarTileProps = {
  day: number;
  winners: AdventWinner[];
};

const CalendarTileRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'ended',
})<{ ended?: boolean }>(({ theme }) => ({
  backgroundColor: '#C3D0E6B2',
  color: '#031839',
  borderRadius: '3px',
  aspectRatio: '1 / 1',
  placeContent: 'center',
  display: 'grid',
  position: 'relative',

  [theme.breakpoints.up('lg')]: {
    border: '2px solid #FFF4E0',
  },

  variants: [
    {
      props: { ended: true },
      style: {
        backgroundColor: '#031839',
        color: 'white',
        cursor: 'pointer',
        [theme.breakpoints.up('lg')]: {
          borderColor: '#FDC564',
        },
      },
    },
  ],
}));

const dateFormatter = new Intl.DateTimeFormat('no', {
  day: 'numeric',
  month: 'long',
});

// const parseAmount = (amount: string) =>
//   parseInt(amount.replace(' ', '').replace('kr', ''));

const SmallButton = styled(ButtonBase)(({ theme }) => ({
  backgroundColor: '#FDC564',
  color: '#031839',
  borderRadius: '99999px',
  padding: '2px 8px',
  fontSize: '0.8rem',
  letterSpacing: '0.02em',
  fontWeight: 600,
  border: '1px solid #FDC564',
  transition: theme.transitions.create(['background-color']),

  '&:hover': {
    backgroundColor: 'white',
  },
}));

type CalendarTileRibbonProps = {
  date: NoDate;
};

const CalendarTileRibbon: React.FC<CalendarTileRibbonProps> = ({ date }) => {
  const { hours, minutes, seconds } = useTimer(date);

  return (
    <Stack
      sx={{
        position: 'absolute',
        bottom: 8,
        left: 0,
        right: 0,
        backgroundColor: '#E84A4A',
        color: 'white',
        py: '2px',
      }}
    >
      <p className="-mb-0.5 typography-v1-caption1">Åpnes om</p>
      <p className="typography-v1-h5">
        <span style={{ fontSize: '0.9em' }}>
          {hours}t : {minutes}m : {seconds}s
        </span>
      </p>
      <RibbonSmallBottom
        sx={{
          scale: 2,
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          transformOrigin: '0 0',
        }}
      />
    </Stack>
  );
};

const CalendarTile: React.FC<CalendarTileProps> = ({ day, winners }) => {
  const hasWinners = winners.length > 0;
  // const amount = winners.reduce(
  //   (acc, winner) => acc + parseAmount(winner.amount),
  //   0,
  // );
  const date = advent2024DrawDates[day - 1];
  const nextDrawDate = getNextAdvent2024DrawDate();
  const isAfterFirstDraw = useIsAfterDate(advent2024DrawDates[0]);
  const [open, setOpen] = useState(false);
  const ended = useIsAfterDate(date);

  if (hasWinners) {
    return (
      <>
        <CalendarTileRoot className={`day-${day}`} ended onClick={() => setOpen(true)}>
          <Stack>
            <p className="typography-v1-caption1">
              <b>{dateFormatter.format(new Date(2024, 11, day))}</b>
            </p>
            <p className="typography-v1-caption1">{winners[0].meta.tileLine1}</p>
            <p className="mb-2 typography-v1-caption1">
              <Gold>{winners[0].meta.tileLine2}</Gold>
            </p>
            <SmallButton onClick={() => setOpen(true)}>Se vinner</SmallButton>
          </Stack>
        </CalendarTileRoot>

        <WinnerDialog day={day} winners={winners} open={open} onClose={() => setOpen(false)} />
      </>
    );
  }

  if (ended) {
    return (
      <>
        <CalendarTileRoot className={`day-${day}`} ended onClick={() => setOpen(true)}>
          <Stack>
            <p className="mb-3 typography-v1-caption1">
              {dateFormatter.format(new Date(2024, 11, day))}
            </p>
            <p className="mb-4 typography-v1-caption1">
              <b style={{ fontSize: '1.1em' }}>Trukket</b>
            </p>
            <SmallButton onClick={() => setOpen(true)}>Se vinner</SmallButton>
          </Stack>
        </CalendarTileRoot>

        <NoWinnerYetDialog day={day} open={open} onClose={() => setOpen(false)} />
      </>
    );
  }

  const Background = {
    6: TileBgCanes,
    8: TileBgSnow,
    13: TileBgGingerbread,
    24: TileBgTree,
  }[day];

  return (
    <CalendarTileRoot className={`day-${day}`}>
      <p className="typography-v1-h1">{day}</p>
      {Background && (
        <Background
          sx={{
            position: 'absolute',
            width: 'auto',
            height: '100%',
            aspectRatio: '1 / 1',
            display: { xs: 'block', lg: 'none' },
          }}
        />
      )}
      {date === nextDrawDate && isAfterFirstDraw && <CalendarTileRibbon date={date} />}
    </CalendarTileRoot>
  );
};

type WinnerDialogProps = {
  day: number;
  winners: AdventWinner[];
  open: boolean;
  onClose: () => void;
};

const WinnerDialog: React.FC<WinnerDialogProps> = ({ day, winners, open, onClose }) => {
  const winner = winners[0];

  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: '#031839',
          color: 'white',
          colorScheme: 'dark',
          p: 2,
          borderRadius: '16px',
          mx: 0,
          width: 'calc(100% - 24px)',
        },
      }}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <IconButton sx={{ ml: 'auto' }} size="small" onClick={onClose}>
        <Close fontSize="small" />
      </IconButton>
      <DialogContent sx={{ pt: 0, pb: 2 }}>
        <Stack>
          <p className="typography-v1-body4">Dagens vinner</p>
          <p className="mb-6 typography-v1-h2">{day}. desember</p>

          {winner.meta.instagramPostUrl ? (
            <Box sx={{ mb: 2, minHeight: { xs: '544px', md: '626px' } }}>
              <InstagramEmbed url={winner.meta.instagramPostUrl} width={326} />
            </Box>
          ) : (
            <p className="mb-8 text-center typography-v1-body4">
              {winner.name}
              <br />
              {winner.age}, {winner.city}
              <br />
              Gratulerer!
            </p>
          )}

          <Button color="yellow" onClick={onClose} sx={{ width: 'auto', minWidth: '200px' }}>
            OK
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

type NoWinnerYetDialogProps = {
  day: number;
  open: boolean;
  onClose: () => void;
};

const NoWinnerYetDialog: React.FC<NoWinnerYetDialogProps> = ({ day, open, onClose }) => {
  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: '#031839',
          color: 'white',
          colorScheme: 'dark',
          p: 2,
          borderRadius: '16px',
          mx: 0,
          width: 'calc(100% - 24px)',
        },
      }}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <IconButton sx={{ ml: 'auto' }} size="small" onClick={onClose}>
        <Close fontSize="small" />
      </IconButton>
      <DialogContent sx={{ pt: 0, pb: 2 }}>
        <Stack>
          <p className="typography-v1-body4">Dagens vinner</p>
          <p className="mb-6 typography-v1-h2">{day}. desember</p>

          <p className="mb-6 text-center typography-v1-body4">
            Luken er åpnet.
            <br />
            Vinneren publiseres så snart vi har
            <br /> oppnådd kontakt.
          </p>

          <Button color="yellow" onClick={onClose} sx={{ width: 'auto', minWidth: '200px' }}>
            OK
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const Gold = styled('span')({ color: '#FDC564', fontWeight: 700 });

const Content = () => {
  return (
    <Box>
      <h1 className="mb-12 text-[2.25rem] leading-[1.2] text-balance sm:text-[3rem] xl:text-[4rem]">
        <Gold>Gjeldfri</Gold> med Scooprs julekalender?
      </h1>

      <p className="mb-8 typography-v1-body3 font-extrabold">Denne trekningen er avsluttet.</p>

      {/* Prize schedule and numbered list were retired when the draw ended. */}

      <CTA
        sx={{ width: 'auto' }}
        onClick={() => {
          sendUserPortalEvent('adv24_competition_cta_top_clicked');
        }}
      />
    </Box>
  );
};

const Illustration = () => {
  return (
    <Box
      sx={{
        gridArea: 'main',
        position: 'relative',
        isolation: 'isolate',
        zIndex: -1,
        overflow: 'hidden',
      }}
    >
      <MountainsBG
        sx={{
          position: 'absolute',
          left: { xs: '-20%', md: 0 },
          right: 0,
          bottom: { xs: 100, sm: 100, md: 90, lg: 50 },
          height: { xs: '220px', sm: '280px', md: '320px', lg: '480px' },
          width: { xs: '150%', md: '120%', lg: '100%' },
        }}
        preserveAspectRatio="none"
      />
      <ForestRight
        sx={{
          position: 'absolute',
          right: 0,
          bottom: 60,
          display: { xs: 'none', lg: 'block' },
        }}
      />
      <ForestLeft
        sx={{
          position: 'absolute',
          left: 0,
          bottom: { xs: -60, md: -40 },
          width: { xs: '100%', md: 'auto' },
          overflow: 'hidden',
        }}
      />
      <Snow
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
        }}
        preserveAspectRatio="none"
      />

      {/* Right */}
      <Tree
        sx={{
          position: 'absolute',
          right: -15,
          bottom: 30,
          scale: 0.5,
          display: { xs: 'none', lg: 'block' },
        }}
      />
      <Tree
        sx={{
          position: 'absolute',
          right: -55,
          bottom: 10,
          scale: 0.5,
          display: { xs: 'none', lg: 'block' },
        }}
      />
      <Tree
        sx={{
          position: 'absolute',
          right: 15,
          bottom: -10,
          scale: 0.5,
          display: { xs: 'none', lg: 'block' },
        }}
      />

      <WhiteHouse
        sx={{
          position: 'absolute',
          left: { xs: 150, sm: 350, md: 480 },
          bottom: { xs: 90, sm: 80 },
          scale: { xs: 0.5, sm: 0.75, md: 1 },
        }}
      />
      <TreeWithLights
        sx={{
          position: 'absolute',
          left: { xs: 280, sm: 505, md: 680 },
          bottom: { xs: 40, sm: 30 },
          scale: { xs: 0.5, sm: 0.6, md: 0.75 },
        }}
      />
      <Snowman
        sx={{
          position: 'absolute',
          left: { xs: 220, sm: 420, md: 560 },
          bottom: { xs: 80, sm: 60, md: 50 },
          scale: { xs: 0.5, sm: 0.75, md: 1 },
        }}
      />

      <Horse
        sx={{
          position: 'absolute',
          left: 830,
          bottom: 20,
          display: { xs: 'none', lg: 'block' },
          scale: 0.8,
        }}
      />

      {/* Red house */}
      {/* Right (taller one) on the right of the house */}
      <Tree
        sx={{
          position: 'absolute',
          left: { xs: 115, sm: 280, md: 380 },
          bottom: 0,
          scale: { xs: 0.3, sm: 0.5, md: 0.75 },
        }}
      />
      {/* Left (shorter one) on the right of the house */}
      <Tree
        sx={{
          position: 'absolute',
          left: { sm: 260, md: 360 },
          bottom: -5,
          scale: { sm: 0.5, md: 0.75 },
          display: { xs: 'none', sm: 'block' },
        }}
      />
      {/* On the left side of the house */}
      <Tree
        sx={{
          position: 'absolute',
          left: { sm: 120, md: 140 },
          bottom: 20,
          scale: { sm: 0.5, md: 0.75 },
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <Tree
        sx={{
          position: 'absolute',
          left: { xs: -15, sm: 35 },
          bottom: 30,
          scale: { xs: 0.5, sm: 0.75, md: 1 },
        }}
      />
      <Tree
        sx={{
          position: 'absolute',
          left: { xs: -55, sm: -20 },
          bottom: { xs: 45, sm: 60 },
          scale: { xs: 0.5, sm: 0.75, md: 1 },
        }}
      />
      <Tree
        sx={{
          position: 'absolute',
          left: { sm: -75 },
          bottom: 40,
          scale: { xs: 0.5, sm: 0.75, md: 1 },
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <RedHouse
        sx={{
          position: 'absolute',
          left: { xs: 35, sm: 130 },
          bottom: 30,
          width: { xs: 160, sm: 250, md: 'auto' },
        }}
      />

      <Snowfall sx={{ position: 'absolute', inset: 0 }} />
      <SnowFront sx={{ position: 'absolute', left: 0, bottom: 0 }} />
      <SnowFrontDark
        sx={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          scale: { xs: 0.5, md: 0.75, xl: 1 },
          transformOrigin: 'bottom right',
        }}
      />
    </Box>
  );
};
